/**
 * แบ่ง transcript ของ Tax Log V2 ออกเป็น "ช่วงของฟอร์ม" เพื่อให้แชทมีหัวคั่นลอยแบบไลน์
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 ปัญหาที่ไฟล์นี้แก้ — **1 session มีได้หลายฟอร์ม** (เคยเจอ 32 ใบใน session เดียว) แต่หน้า
 * `/TaxLogV2/{sessionId}` แสดง transcript ของ **ทั้ง session** เป็นสายเดียวไม่มีรอยต่อ คนอ่านจึงบอกไม่ได้
 * ว่าบับเบิลที่กำลังดูอยู่เป็นของฟอร์มใบไหน (ดู header ของ `Model/AppModel/TaxLogV2Models.cs`)
 *
 * 🔴 **เทิร์น production ไม่มี FormID ของตัวเอง** — `dbo.Log_Message` ไม่มีคอลัมน์นั้น และ DTO ก็เขียน
 * กำกับไว้ตรง ๆ ว่า `FormID = 0` สำหรับแถว production ("not resolvable per turn") ⇒ ที่นี่จึง **อนุมาน
 * จากเวลา** ไม่ใช่อ่านค่าจริง ผู้เรียกต้องติดป้ายให้คนอ่านรู้ว่าเป็นค่าประมาณ (ดู `inferred` ที่ผู้เรียกแนบเอง)
 * ส่วนเทิร์น playground มี `formID` จริงมากับแถว ⇒ ใช้ค่านั้นตรง ๆ ไม่ต้องเดา
 *
 * ── กติกาการอนุมาน (สำคัญ อ่านก่อนแก้) ────────────────────────────────────────────────────────────
 * แชทหนึ่ง session เดินทีละเทิร์น (ไม่มีสองเทิร์นวิ่งพร้อมกัน) และแถว `Log_Message` ถูก **สร้างก่อน**
 * flow จะรัน (ดู `FlowEngineController.PreCreateDebugLogMessage` ที่เขียนว่า "mirrors the normal chat
 * pattern where Log_Message is created first") ⇒ ฟอร์มที่ถูกสร้างระหว่างเทิร์นหนึ่ง จะมี `CreatedDate`
 * **ช้ากว่า** `CreateDate` ของเทิร์นนั้นเสมอ
 *
 * ดังนั้น "เทิร์นที่สร้างฟอร์ม" = เทิร์น**สุดท้ายที่เริ่มก่อน**เวลาสร้างฟอร์ม และช่วงของฟอร์มใบใหม่
 * ต้องเริ่มนับ **ที่เทิร์นนั้น** (ไม่ใช่เทิร์นถัดไป) เพราะคำตอบของเทิร์นนั้นคือคำถามข้อแรกของฟอร์มใหม่แล้ว
 *
 * ⚠️ ถ้าฟอร์มถูกสร้างในช่องว่างที่ห่างจากเทิร์นก่อนหน้าเกิน {@link CREATING_TURN_WINDOW_MS} แปลว่ามันไม่ได้
 * ถูกสร้างโดยเทิร์นนั้น (คนละ session / นอกหน้าที่โหลดมา) ⇒ ให้เริ่มที่เทิร์น**ถัดไป**แทน ไม่งั้นฟอร์มที่เพิ่ง
 * สร้างหลังเทิร์นสุดท้ายของหน้าที่โหลดมา จะแย่งป้ายของเทิร์นท้าย ๆ ไปทั้งที่ยังไม่มีบทสนทนาของมันเลย
 *
 * ⚠️ ข้อจำกัดที่ยอมรับ: session ที่ **ย้อนกลับ**ไปทำฟอร์มเก่ากลางคัน (สร้าง A แล้วค่อย resume B ที่เก่ากว่า)
 * จะถูกอ่านเป็น "B ก่อน แล้ว A" ตามเวลาสร้าง — เวลาไม่ได้บอกลำดับการเดินในกรณีนั้น และไม่มีข้อมูลอื่นให้ใช้
 */

import { parseUtcDate, fmtThaiDate } from './thaiTime.js';

/**
 * ช่องว่างสูงสุดระหว่าง "เทิร์นเริ่ม" กับ "ฟอร์มถูกสร้าง" ที่ยังนับว่าเป็นเทิร์นเดียวกัน
 * 10 นาที = เผื่อเทิร์นที่ยาวที่สุดเท่าที่วัดได้ (เทิร์น CALCULATE เคยกินเวลาเป็นนาที) แบบมีเหลือ
 */
export const CREATING_TURN_WINDOW_MS = 10 * 60 * 1000;

/** `formID` ของบับเบิล/เทิร์นที่เกิด **ก่อน** ฟอร์มใบแรกของ session (ทักทาย / คุยเรื่องอื่นก่อนเข้าฟอร์ม) */
export const NO_FORM_ID = 0;

function tsOf(value) {
  const d = parseUtcDate(value);
  return d ? d.getTime() : null;
}

/**
 * index ของเทิร์นที่ช่วงของฟอร์มนี้เริ่มนับ
 * @param {(number|null)[]} times เวลาเริ่มของแต่ละเทิร์น (ms, เรียงเก่า→ใหม่ตามลำดับ transcript)
 * @param {number} createdAt เวลาที่ฟอร์มถูกสร้าง (ms)
 * @returns {number} index (อาจ = `times.length` ⇒ ฟอร์มนี้ยังไม่มีเทิร์นในหน้าที่โหลดมา)
 */
function anchorIndexFor(times, createdAt) {
  let last = -1;
  for (let i = 0; i < times.length; i += 1) {
    if (times[i] != null && times[i] <= createdAt) last = i;
  }
  // ไม่มีเทิร์นไหนเริ่มก่อนฟอร์มนี้เลย ⇒ ฟอร์มเก่ากว่าทั้ง transcript (ถูก resume มา) ⇒ มีผลตั้งแต่เทิร์นแรก
  if (last < 0) return 0;
  return createdAt - times[last] <= CREATING_TURN_WINDOW_MS ? last : last + 1;
}

/**
 * จับคู่ "เทิร์น → ฟอร์ม" ของทั้ง transcript
 *
 * @param {Array} turns แถวจาก `GET /sessions/{id}/turns` (เก่า→ใหม่) — ใช้ `turnKey`, `createdDate`, `formID`
 * @param {Array} forms `TaxLogV2SessionDetailDto.Forms` — ใช้ `formID`, `createdDate`
 * @returns {Map<string, number>} `turnKey` → `formID` (`0` = ยังไม่เข้าฟอร์มใบไหน)
 */
export function resolveTurnFormIds(turns, forms) {
  const list = Array.isArray(turns) ? turns : [];
  const map = new Map();
  if (!list.length) return map;

  const times = list.map((t) => tsOf(t && t.createdDate));

  const anchors = (Array.isArray(forms) ? forms : [])
    .filter((f) => f && Number(f.formID) > 0)
    .map((f) => ({ formID: Number(f.formID), createdAt: tsOf(f.createdDate) }))
    .filter((f) => f.createdAt != null)
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((f) => ({ ...f, index: anchorIndexFor(times, f.createdAt) }))
    // เรียงตามจุดเริ่มจริงบนจอ — ฟอร์มสองใบที่ลงที่ index เดียวกัน ให้ใบที่สร้างทีหลังชนะ (มันเริ่มทีหลัง)
    .sort((a, b) => a.index - b.index || a.createdAt - b.createdAt);

  let ai = 0;
  let current = NO_FORM_ID;
  list.forEach((t, i) => {
    while (ai < anchors.length && anchors[ai].index <= i) {
      current = anchors[ai].formID;
      ai += 1;
    }
    if (!t || !t.turnKey) return;
    // 🔴 เทิร์น playground บอกฟอร์มของตัวเองได้ ⇒ ค่าจริงชนะค่าที่อนุมานเสมอ
    const own = Number(t.formID) || 0;
    map.set(t.turnKey, own > 0 ? own : current);
  });

  return map;
}

/**
 * ยุบบับเบิลที่ติดกันให้เป็น "ช่วง" ตาม (ฟอร์ม × วันตามเวลาไทย) — 1 ช่วง = 1 หัวคั่นลอยในแชท
 *
 * 🔴 ตัดตาม **ฟอร์ม หรือ วันที่เปลี่ยน** อย่างใดอย่างหนึ่ง: ฟอร์มเดียวที่คุยข้ามวันต้องมีหัวใหม่ของวันใหม่
 * (แบบเดียวกับเส้นคั่นวันของไลน์) และฟอร์มที่เปลี่ยนกลางวันเดียวกันก็ต้องมีหัวใหม่เช่นกัน
 *
 * ⚠️ บับเบิลที่ไม่มี `time` จะถูกนับรวมอยู่ในช่วงปัจจุบัน (ไม่เปิดช่วงใหม่) — เวลาที่หายไปไม่ใช่เหตุผล
 * ที่ดีพอจะแตกหัวคั่นเพิ่ม
 *
 * @param {Array} bubbles บับเบิลจาก `TaxLogV2Detail.bubbles()` — ใช้ `key`, `time`, `formID`, `formTitle`, `formInferred`
 * @returns {Array} `{ key, formID, formTitle, formInferred, day, time, bubbles[] }[]`
 */
export function buildChatSegments(bubbles) {
  const list = Array.isArray(bubbles) ? bubbles : [];
  const out = [];
  let seg = null;

  list.forEach((b) => {
    if (!b) return;
    const formID = Number(b.formID) || NO_FORM_ID;
    const day = b.time ? fmtThaiDate(b.time, '') : (seg ? seg.day : '');
    if (!seg || seg.formID !== formID || (day && seg.day && day !== seg.day)) {
      seg = {
        key: `seg-${formID}-${day || 'na'}-${b.key}`,
        formID,
        formTitle: b.formTitle || '',
        formInferred: Boolean(b.formInferred),
        day,
        // เวลาของบับเบิลแรกในช่วง = "ช่วงนี้เริ่มเมื่อไร" ซึ่งเป็นสิ่งที่หัวคั่นบอก
        time: b.time || null,
        bubbles: [],
      };
      out.push(seg);
    } else if (!seg.day && day) {
      seg.day = day;
    }
    seg.bubbles.push(b);
  });

  return out;
}
